import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import type { Photo, SpriteSheet } from './AevocamViewer'
import { imagePrefetch, type ImagePrefetchImages, type ImagePrefetchLoaded } from './imagePrefetch'
import { useAevocamPlayer } from './useAevocamPlayer'

// React-style setter type
type SetStateAction<T> = T | ((prev: T) => T)
type Dispatch<T> = (value: SetStateAction<T>) => void

export type SpriteSet = 'low' | 'medium'

const LOOK_AHEAD_IN_HIGH_GEAR = 5

//------------------------------------------------------------
// AevocamSprite component - handles sprite sheet preview display
//------------------------------------------------------------
type AevocamSpriteProps = {
  photos: (Photo | undefined)[]
  spriteSheets: SpriteSheet[]
  setSpritesHighGear: Dispatch<boolean>
  viewWidth: number
  viewHeight: number
  spriteSet: SpriteSet
  highGear: boolean
  token: string
}

export function AevocamSprite({
  photos,
  spriteSheets,
  setSpritesHighGear,
  viewWidth,
  viewHeight,
  spriteSet,
  highGear,
  token,
}: AevocamSpriteProps) {
  const { tenantId, feedId, currentIndex, playReverse, cdnUrl, proactiveLoading } = useAevocamPlayer()
  const [spriteSheetsLoaded, setSpriteSheetsLoaded] = useState<ImagePrefetchLoaded>({})
  
  // Iteration ref for bailing out of prefetch when props change
  const prefetchIteration = useRef({ current: 0 })
  
  // Counter for concurrent image loading for this component
  const activelyLoadingRef = useRef({ current: 0 })

  //------------------------------------------------------------
  // Helper function to get sprite sheet index for a photo
  const getSpriteSheetIndex = useCallback((photo: Photo | undefined): number | undefined => {
    if (!photo) return undefined
    if (spriteSet === 'low') {
      return spriteSheets.findIndex(ss => ss.sq === photo.slq && ss.sc === photo.slc)
    } else {
      return spriteSheets.findIndex(ss => ss.sq === photo.smq && ss.sc === photo.smc)
    }
  }, [spriteSheets, spriteSet])

  //------------------------------------------------------------
  // Get current photo and its sprite sheet
  const currentPhoto = photos[currentIndex]
  const currentSpriteSheetIndex = useMemo(() => {
    return getSpriteSheetIndex(currentPhoto)
  }, [currentPhoto, getSpriteSheetIndex])

  //------------------------------------------------------------
  // Convert sprite sheets to ImagePrefetchImages format
  const prefetchImages: ImagePrefetchImages = useMemo(() => {
    const base = (ss: SpriteSheet) =>
      `${cdnUrl}/${tenantId}/f/${feedId}/sprite/${ss.sc}/${ss.sq}.${ss.ss}`
    return spriteSheets.map(ss => ({
      key: String(ss.sq),
      url: `${base(ss)}?tk=${encodeURIComponent(token)}`,
    }))
  }, [spriteSheets, tenantId, feedId, cdnUrl, token])

  //------------------------------------------------------------
  // Prefetch sprite sheets using imagePrefetch function
  useEffect(() => {
    // Don't prefetch when user has been inactive (saves loading the full image set)
    if (!proactiveLoading) return
    // Don't load until we're allowed (highGear prop is true)
    if (!highGear) return
    if (spriteSheets.length === 0) return
    if (currentSpriteSheetIndex === undefined || currentSpriteSheetIndex < 0) return

    // Increment iteration to cancel any previous prefetch
    prefetchIteration.current.current++

    // Start prefetching from currentSpriteSheetIndex in the playback direction
    imagePrefetch({
      iteration: prefetchIteration.current,
      activelyLoading: activelyLoadingRef.current,
      startAtIndex: currentSpriteSheetIndex,
      reverse: playReverse,
      simultaneous: 1, // Load sprite sheets one at a time
      lookAhead: spriteSet === 'low'
        ? -1 // infinity
        : LOOK_AHEAD_IN_HIGH_GEAR,
      images: prefetchImages,
      loaded: spriteSheetsLoaded,
      setLoaded: setSpriteSheetsLoaded,
      setHighGear: setSpritesHighGear,
    })

    // Cleanup: increment iteration to cancel prefetch on unmount or prop change
    return () => {
      prefetchIteration.current.current++
    }
  }, [proactiveLoading, highGear, currentSpriteSheetIndex, playReverse, prefetchImages, setSpritesHighGear, spriteSheets.length])

  //------------------------------------------------------------
  // Use the sprite sheet we already found above
  const spriteSheet = currentSpriteSheetIndex !== undefined
    ? spriteSheets[currentSpriteSheetIndex]
    : undefined

  //------------------------------------------------------------
  const handleLoad = useCallback((ev: React.SyntheticEvent<HTMLImageElement>) => {
    const el = ev.target as HTMLImageElement;
    el.setAttribute('data-loaded', 'true');
  }, [currentSpriteSheetIndex])

  //------------------------------------------------------------
  const handleError = useCallback((ev: React.SyntheticEvent<HTMLImageElement>) => {
    const el = ev.target as HTMLImageElement;
    el.setAttribute('data-loaded', 'false');
  }, [currentSpriteSheetIndex])

  //------------------------------------------------------------
  // Calculate grid row and column for current photo
  // Get the slot property based on spriteSet
  const slot = spriteSet === 'low' ? currentPhoto?.sll : currentPhoto?.sml
  const gridRow = currentPhoto && slot !== null && slot !== undefined && spriteSheet
    ? Math.floor(slot / spriteSheet.sg)
    : null
  const gridCol = currentPhoto && slot !== null && slot !== undefined && spriteSheet
    ? slot % spriteSheet.sg
    : null

  const spriteScale = spriteSheet
    ? (viewWidth * spriteSheet.sg) / spriteSheet.w
    : 1

  if (!spriteSheet || gridCol === null || gridRow === null) {
    return (
      <div className='AevocamSprite'>
        Loading ...
      </div>
    )
  }

  //------------------------------------------------------------
  // Render
  return (
    <div className='AevocamSprite'>
      <div
        style={{
          width: `${viewWidth}px`,
          height: `${viewHeight}px`,
        }}
      >
        <img
          key={spriteSheet.sq}
          data-sq={spriteSheet.sq}
          //alt='Grid of low-res versions'
          src={`${cdnUrl}/${tenantId}/f/${feedId}/sprite/${spriteSheet.sc}/${spriteSheet.sq}.${spriteSheet.ss}?tk=${encodeURIComponent(token)}`}
          style={{
            width: spriteSheet.w,
            height: spriteSheet.h,
            objectFit: 'none',
            objectPosition: `-${gridCol * (spriteSheet.w / spriteSheet.sg)}px -${gridRow * (spriteSheet.h / spriteSheet.sg)}px`,
            transform: `scale(${spriteScale})`,
            transformOrigin: 'top left',
          }}
          onLoad={handleLoad}
          onError={handleError}
        />
      </div>
    </div>
  )
}

